import { VdpElement, Point } from '../types';
import { getStarSvgPath, getPolygonSvgPath } from './shapeAndFormatUtils';
import { pointsToSvgPath } from './vectorPathUtils';
import polygonClipping from 'polygon-clipping';

type Ring = [number, number][];
type Polygon = Ring[];
type MultiPolygon = Polygon[];

/**
 * Samples a sequence of Point objects (including cubic bezier curves) into a closed Ring
 */
function pointsToPolygonRing(points: Point[], offsetX: number = 0, offsetY: number = 0, closed: boolean = true): Ring {
  if (!points || points.length < 2) return [];
  const ring: Ring = [];

  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    const prev = i > 0 ? points[i - 1] : null;
    if (prev && (prev.cp2 || p.cp1)) {
      const p0 = [prev.x + offsetX, prev.y + offsetY];
      const p1 = prev.cp2 ? [prev.cp2.x + offsetX, prev.cp2.y + offsetY] : p0;
      const p2 = p.cp1 ? [p.cp1.x + offsetX, p.cp1.y + offsetY] : [p.x + offsetX, p.y + offsetY];
      const p3 = [p.x + offsetX, p.y + offsetY];
      const steps = 10;
      for (let s = 1; s <= steps; s++) {
        const t = s / steps;
        const mt = 1 - t;
        const sx = mt * mt * mt * p0[0] + 3 * mt * mt * t * p1[0] + 3 * mt * t * t * p2[0] + t * t * t * p3[0];
        const sy = mt * mt * mt * p0[1] + 3 * mt * mt * t * p1[1] + 3 * mt * t * t * p2[1] + t * t * t * p3[1];
        ring.push([sx, sy]);
      }
    } else {
      ring.push([p.x + offsetX, p.y + offsetY]);
    }
  }

  if (closed && points.length > 2) {
    const last = points[points.length - 1];
    const first = points[0];
    if (last.cp2 || first.cp1) {
      const p0 = [last.x + offsetX, last.y + offsetY];
      const p1 = last.cp2 ? [last.cp2.x + offsetX, last.cp2.y + offsetY] : p0;
      const p2 = first.cp1 ? [first.cp1.x + offsetX, first.cp1.y + offsetY] : [first.x + offsetX, first.y + offsetY];
      const p3 = [first.x + offsetX, first.y + offsetY];
      const steps = 10;
      for (let s = 1; s <= steps; s++) {
        const t = s / steps;
        const mt = 1 - t;
        const sx = mt * mt * mt * p0[0] + 3 * mt * mt * t * p1[0] + 3 * mt * t * t * p2[0] + t * t * t * p3[0];
        const sy = mt * mt * mt * p0[1] + 3 * mt * mt * t * p1[1] + 3 * mt * t * t * p2[1] + t * t * t * p3[1];
        ring.push([sx, sy]);
      }
    }
  }

  if (ring.length > 2) {
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (Math.hypot(first[0] - last[0], first[1] - last[1]) > 0.05) {
      ring.push([first[0], first[1]]);
    }
  }
  return ring;
}

/**
 * Parses SVG path 'd' string into closed Polygon rings
 */
function svgPathToRings(d: string, offsetX: number = 0, offsetY: number = 0): Polygon {
  if (!d) return [];
  const rings: Polygon = [];
  let curRing: Ring = [];
  let curX = 0, curY = 0;
  let startX = 0, startY = 0;

  const cmdRegex = /([a-df-z])|(-?\d*\.?\d+(?:e[-+]?\d+)?)/gi;
  const tokens: string[] = [];
  let m;
  while ((m = cmdRegex.exec(d)) !== null) {
    tokens.push(m[0]);
  }

  let i = 0;
  let curCmd = 'M';
  while (i < tokens.length) {
    const token = tokens[i];
    if (/^[a-df-z]$/i.test(token)) {
      curCmd = token;
      i++;
    }

    const isRel = curCmd === curCmd.toLowerCase();
    const cmd = curCmd.toUpperCase();

    if (cmd === 'M') {
      const x = parseFloat(tokens[i++]) + (isRel ? curX : 0) + (isRel ? 0 : offsetX);
      const y = parseFloat(tokens[i++]) + (isRel ? curY : 0) + (isRel ? 0 : offsetY);
      if (curRing.length > 2) {
        if (Math.hypot(curRing[0][0] - curRing[curRing.length - 1][0], curRing[0][1] - curRing[curRing.length - 1][1]) > 0.05) {
          curRing.push([curRing[0][0], curRing[0][1]]);
        }
        rings.push(curRing);
      }
      curRing = [[x, y]];
      curX = x - offsetX;
      curY = y - offsetY;
      startX = curX;
      startY = curY;
      curCmd = isRel ? 'l' : 'L';
    } else if (cmd === 'L') {
      const x = parseFloat(tokens[i++]) + (isRel ? curX : 0) + (isRel ? 0 : offsetX);
      const y = parseFloat(tokens[i++]) + (isRel ? curY : 0) + (isRel ? 0 : offsetY);
      curRing.push([x, y]);
      curX = x - offsetX;
      curY = y - offsetY;
    } else if (cmd === 'H') {
      const x = parseFloat(tokens[i++]) + (isRel ? curX : 0) + (isRel ? 0 : offsetX);
      const y = curY + offsetY;
      curRing.push([x, y]);
      curX = x - offsetX;
    } else if (cmd === 'V') {
      const x = curX + offsetX;
      const y = parseFloat(tokens[i++]) + (isRel ? curY : 0) + (isRel ? 0 : offsetY);
      curRing.push([x, y]);
      curY = y - offsetY;
    } else if (cmd === 'C') {
      const cp1x = parseFloat(tokens[i++]) + (isRel ? curX : 0) + (isRel ? 0 : offsetX);
      const cp1y = parseFloat(tokens[i++]) + (isRel ? curY : 0) + (isRel ? 0 : offsetY);
      const cp2x = parseFloat(tokens[i++]) + (isRel ? curX : 0) + (isRel ? 0 : offsetX);
      const cp2y = parseFloat(tokens[i++]) + (isRel ? curY : 0) + (isRel ? 0 : offsetY);
      const endX = parseFloat(tokens[i++]) + (isRel ? curX : 0) + (isRel ? 0 : offsetX);
      const endY = parseFloat(tokens[i++]) + (isRel ? curY : 0) + (isRel ? 0 : offsetY);
      const p0 = [curX + offsetX, curY + offsetY];
      const p1 = [cp1x, cp1y];
      const p2 = [cp2x, cp2y];
      const p3 = [endX, endY];
      const steps = 8;
      for (let s = 1; s <= steps; s++) {
        const t = s / steps;
        const mt = 1 - t;
        const sx = mt * mt * mt * p0[0] + 3 * mt * mt * t * p1[0] + 3 * mt * t * t * p2[0] + t * t * t * p3[0];
        const sy = mt * mt * mt * p0[1] + 3 * mt * mt * t * p1[1] + 3 * mt * t * t * p2[1] + t * t * t * p3[1];
        curRing.push([sx, sy]);
      }
      curX = endX - offsetX;
      curY = endY - offsetY;
    } else if (cmd === 'Z') {
      if (curRing.length > 0) {
        curRing.push([curRing[0][0], curRing[0][1]]);
      }
      curX = startX;
      curY = startY;
    } else {
      i++;
    }
  }

  if (curRing.length > 2) {
    if (Math.hypot(curRing[0][0] - curRing[curRing.length - 1][0], curRing[0][1] - curRing[curRing.length - 1][1]) > 0.05) {
      curRing.push([curRing[0][0], curRing[0][1]]);
    }
    rings.push(curRing);
  }
  return rings;
}

/**
 * Converts any VdpElement into a polygon clipping representation
 */
export function elementToPolygon(el: VdpElement): Polygon {
  const x = el.x || 0;
  const y = el.y || 0;
  const w = Math.max(1, el.width || 40);
  const h = Math.max(1, el.height || 40);
  const rx = el.cornerRadius || el.borderRadius || 0;

  // 1. Explicit vector points (Pen / Pencil tool)
  if (el.points && el.points.length > 2) {
    const ring = pointsToPolygonRing(el.points, x, y, el.closedPath ?? true);
    if (ring.length >= 3) return [ring];
  }

  // 2. Explicit SVG path data
  if ((el.type === 'path' || el.type === 'pen') && (el.pathData || el.pathD)) {
    const d = (el.pathData || el.pathD) as string;
    const rings = svgPathToRings(d, x, y);
    if (rings.length > 0) return rings;
  }

  const shapeType = (el.shapeType || el.type || 'rectangle').toLowerCase();

  // 3. Circle / Oval / Ellipse
  if (shapeType === 'circle' || shapeType === 'oval' || shapeType === 'ellipse') {
    const cx = x + w / 2;
    const cy = y + h / 2;
    const rX = w / 2;
    const rY = h / 2;
    const steps = 48;
    const ring: Ring = [];
    for (let s = 0; s < steps; s++) {
      const a = (s / steps) * Math.PI * 2;
      ring.push([cx + rX * Math.cos(a), cy + rY * Math.sin(a)]);
    }
    ring.push([ring[0][0], ring[0][1]]);
    return [ring];
  }

  // 4. Triangle
  if (shapeType === 'triangle') {
    return [[
      [x + w / 2, y],
      [x + w, y + h],
      [x, y + h],
      [x + w / 2, y]
    ]];
  }

  // 5. Diamond
  if (shapeType === 'diamond') {
    return [[
      [x + w / 2, y],
      [x + w, y + h / 2],
      [x + w / 2, y + h],
      [x, y + h / 2],
      [x + w / 2, y]
    ]];
  }

  // 6. Star
  if (shapeType === 'star') {
    const cx = x + w / 2;
    const cy = y + h / 2;
    const outerR = Math.min(w, h) / 2;
    const innerR = outerR * (el.starInnerRadiusRatio || 0.4);
    const count = el.starPoints || el.pointsCount || 5;
    const ring: Ring = [];
    for (let s = 0; s < count * 2; s++) {
      const a = (s * Math.PI) / count - Math.PI / 2;
      const r = s % 2 === 0 ? outerR : innerR;
      ring.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
    }
    ring.push([ring[0][0], ring[0][1]]);
    return [ring];
  }

  // 7. Polygon / Hexagon
  if (shapeType === 'polygon' || shapeType === 'hexagon') {
    const cx = x + w / 2;
    const cy = y + h / 2;
    const r = Math.min(w, h) / 2;
    const sides = el.polygonSides || el.pointsCount || 6;
    const ring: Ring = [];
    for (let s = 0; s < sides; s++) {
      const a = (s * 2 * Math.PI) / sides - Math.PI / 2;
      ring.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
    }
    ring.push([ring[0][0], ring[0][1]]);
    return [ring];
  }

  // 8. Arrow
  if (shapeType === 'arrow') {
    const headW = w * 0.4;
    const shaftH = h * 0.4;
    const shaftTop = y + (h - shaftH) / 2;
    const shaftBottom = shaftTop + shaftH;
    const shaftEnd = x + w - headW;
    return [[
      [x, shaftTop],
      [shaftEnd, shaftTop],
      [shaftEnd, y],
      [x + w, y + h / 2],
      [shaftEnd, y + h],
      [shaftEnd, shaftBottom],
      [x, shaftBottom],
      [x, shaftTop]
    ]];
  }

  // 9. Heart
  if (shapeType === 'heart') {
    const d = elementToSvgPathD(el);
    const rings = svgPathToRings(d, 0, 0);
    if (rings.length > 0) return rings;
  }

  // 10. Rounded Rectangle
  if (rx > 0) {
    const r = Math.min(rx, w / 2, h / 2);
    const steps = 8;
    const ring: Ring = [];
    ring.push([x + r, y]);
    ring.push([x + w - r, y]);
    for (let s = 1; s <= steps; s++) {
      const a = -Math.PI / 2 + (s / steps) * (Math.PI / 2);
      ring.push([x + w - r + r * Math.cos(a), y + r + r * Math.sin(a)]);
    }
    ring.push([x + w, y + h - r]);
    for (let s = 1; s <= steps; s++) {
      const a = (s / steps) * (Math.PI / 2);
      ring.push([x + w - r + r * Math.cos(a), y + h - r + r * Math.sin(a)]);
    }
    ring.push([x + r, y + h]);
    for (let s = 1; s <= steps; s++) {
      const a = Math.PI / 2 + (s / steps) * (Math.PI / 2);
      ring.push([x + r + r * Math.cos(a), y + h - r + r * Math.sin(a)]);
    }
    ring.push([x, y + r]);
    for (let s = 1; s <= steps; s++) {
      const a = Math.PI + (s / steps) * (Math.PI / 2);
      ring.push([x + r + r * Math.cos(a), y + r + r * Math.sin(a)]);
    }
    ring.push([ring[0][0], ring[0][1]]);
    return [ring];
  }

  // 11. Standard Sharp Rectangle
  return [[
    [x, y],
    [x + w, y],
    [x + w, y + h],
    [x, y + h],
    [x, y]
  ]];
}

/**
 * Converts MultiPolygon from polygon-clipping back to tight SVG path data and bounds
 */
function multiPolygonToPathData(multiPoly: MultiPolygon): {
  pathD: string;
  points: Point[];
  minX: number;
  minY: number;
  width: number;
  height: number;
} {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

  for (const poly of multiPoly) {
    for (const ring of poly) {
      for (const pt of ring) {
        if (pt[0] < minX) minX = pt[0];
        if (pt[1] < minY) minY = pt[1];
        if (pt[0] > maxX) maxX = pt[0];
        if (pt[1] > maxY) maxY = pt[1];
      }
    }
  }

  if (!isFinite(minX)) {
    return { pathD: '', points: [], minX: 0, minY: 0, width: 0, height: 0 };
  }

  const width = Math.max(1, maxX - minX);
  const height = Math.max(1, maxY - minY);

  const parts: string[] = [];
  const points: Point[] = [];

  for (const poly of multiPoly) {
    for (const ring of poly) {
      if (!ring || ring.length < 3) continue;
      const start = ring[0];
      const relStartX = Math.round((start[0] - minX) * 100) / 100;
      const relStartY = Math.round((start[1] - minY) * 100) / 100;
      let d = `M ${relStartX} ${relStartY}`;

      for (let i = 1; i < ring.length; i++) {
        const pt = ring[i];
        const rx = Math.round((pt[0] - minX) * 100) / 100;
        const ry = Math.round((pt[1] - minY) * 100) / 100;
        d += ` L ${rx} ${ry}`;
        if (points.length < 120) {
          points.push({ x: rx, y: ry, type: 'corner' });
        }
      }
      d += ' Z';
      parts.push(d);
    }
  }

  return {
    pathD: parts.join(' '),
    points,
    minX: Math.round(minX),
    minY: Math.round(minY),
    width: Math.round(width),
    height: Math.round(height)
  };
}


/**
 * Converts any shape element (rectangle, circle, star, polygon, path, triangle, arrow, badge)
 * into a standalone, absolute SVG path 'd' string in canvas coordinates.
 */
export function elementToSvgPathD(el: VdpElement): string {
  const x = el.x || 0;
  const y = el.y || 0;
  const w = Math.max(1, el.width || 40);
  const h = Math.max(1, el.height || 40);
  const rx = el.cornerRadius || el.borderRadius || 0;

  // If element already has an explicit pathD or pathData
  const existingPath = el.pathD || el.pathData;
  if (existingPath && existingPath.trim().length > 0) {
    // If element is placed at x, y and path is relative
    if (x === 0 && y === 0) return existingPath;
    return shiftSvgPathCoordinates(existingPath, x, y);
  }

  // If element has points (Pen / Pencil vector path)
  if (el.points && el.points.length > 0) {
    const absPath = pointsToSvgPath(el.points, el.closedPath ?? true, true);
    return shiftSvgPathCoordinates(absPath, x, y);
  }

  const shapeType = (el.shapeType || el.type || 'rectangle').toLowerCase();

  if (shapeType === 'circle' || shapeType === 'oval' || shapeType === 'ellipse') {
    const cx = x + w / 2;
    const cy = y + h / 2;
    const rX = w / 2;
    const rY = h / 2;
    return `M ${cx - rX} ${cy} A ${rX} ${rY} 0 1 0 ${cx + rX} ${cy} A ${rX} ${rY} 0 1 0 ${cx - rX} ${cy} Z`;
  }

  if (shapeType === 'star') {
    const relD = getStarSvgPath(w, h, el.starPoints || el.pointsCount || 5, el.starInnerRadiusRatio || 0.4, rx);
    return shiftSvgPathCoordinates(relD, x, y);
  }

  if (shapeType === 'polygon' || shapeType === 'hexagon') {
    const relD = getPolygonSvgPath(w, h, el.polygonSides || el.pointsCount || 6, rx);
    return shiftSvgPathCoordinates(relD, x, y);
  }

  if (shapeType === 'triangle') {
    return `M ${x + w / 2} ${y} L ${x + w} ${y + h} L ${x} ${y + h} Z`;
  }

  if (shapeType === 'diamond') {
    return `M ${x + w / 2} ${y} L ${x + w} ${y + h / 2} L ${x + w / 2} ${y + h} L ${x} ${y + h / 2} Z`;
  }

  if (shapeType === 'heart') {
    const topCurveHeight = h * 0.3;
    return `M ${x + w / 2} ${y + h} C ${x} ${y + h / 2}, ${x} ${y}, ${x + w / 2} ${y + topCurveHeight} C ${x + w} ${y}, ${x + w} ${y + h / 2}, ${x + w / 2} ${y + h} Z`;
  }

  if (shapeType === 'arrow') {
    const headW = w * 0.4;
    const shaftH = h * 0.4;
    const shaftTop = y + (h - shaftH) / 2;
    const shaftBottom = shaftTop + shaftH;
    const shaftEnd = x + w - headW;
    return `M ${x} ${shaftTop} L ${shaftEnd} ${shaftTop} L ${shaftEnd} ${y} L ${x + w} ${y + h / 2} L ${shaftEnd} ${y + h} L ${shaftEnd} ${shaftBottom} L ${x} ${shaftBottom} Z`;
  }

  // Default Rounded or Sharp Rectangle
  if (rx > 0) {
    const r = Math.min(rx, w / 2, h / 2);
    return `M ${x + r} ${y} h ${w - 2 * r} a ${r} ${r} 0 0 1 ${r} ${r} v ${h - 2 * r} a ${r} ${r} 0 0 1 ${-r} ${r} h ${-(w - 2 * r)} a ${r} ${r} 0 0 1 ${-r} ${-r} v ${-(h - 2 * r)} a ${r} ${r} 0 0 1 ${r} ${-r} Z`;
  }

  return `M ${x} ${y} h ${w} v ${h} h ${-w} Z`;
}

/**
 * Shift all coordinate values in an SVG path 'd' string by (dx, dy).
 */
export function shiftSvgPathCoordinates(d: string, dx: number, dy: number): string {
  if (!d || (dx === 0 && dy === 0)) return d;
  return d.replace(/([M L Q C A Z])|(-?\d+(?:\.\d+)?)\s*,?\s*(-?\d+(?:\.\d+)?)/gi, (match, cmd, xStr, yStr) => {
    if (cmd) return cmd;
    if (xStr !== undefined && yStr !== undefined) {
      const nx = (parseFloat(xStr) + dx).toFixed(2);
      const ny = (parseFloat(yStr) + dy).toFixed(2);
      return `${nx} ${ny}`;
    }
    return match;
  });
}

/**
 * Reverses the winding order of an SVG path string for negative cutout subtraction.
 */
function reverseSvgPathWinding(d: string): string {
  if (!d) return d;
  const tokens = d.match(/([M L Q C A Z])|(-?\d+(?:\.\d+)?)/gi);
  if (!tokens || tokens.length === 0) return d;

  // Simple reverse path segment construction
  const pts: { x: number; y: number }[] = [];
  let currX = 0, currY = 0;
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    if (t === 'M' || t === 'L') {
      const x = parseFloat(tokens[i + 1] || '0');
      const y = parseFloat(tokens[i + 2] || '0');
      pts.push({ x, y });
      currX = x;
      currY = y;
      i += 2;
    }
  }

  if (pts.length < 2) return d;
  const rev = pts.slice().reverse();
  let revD = `M ${rev[0].x} ${rev[0].y}`;
  for (let i = 1; i < rev.length; i++) {
    revD += ` L ${rev[i].x} ${rev[i].y}`;
  }
  return revD + ' Z';
}

/**
 * Perform Pathfinder Boolean Operation on selected VdpElements using true Martinez-Rueda polygon boolean clipping.
 * Correctly computes Union, Subtract (Minus Front), Intersect, and Exclude (XOR) without extra artifacts or duplicate geometry.
 */
export function applyPathfinderOperation(
  selectedElements: VdpElement[],
  allElements: VdpElement[],
  mode: 'unite' | 'subtract' | 'intersect' | 'exclude' | 'divide' | 'merge' | 'minus-back' | 'trim' | 'crop' | 'outline'
): VdpElement[] {
  if (selectedElements.length < 2) return allElements;

  // Sort selected elements by zIndex / layer order (bottom-to-top)
  const sortedSelected = [...selectedElements].sort((a, b) => {
    const idxA = allElements.findIndex(e => e.id === a.id);
    const idxB = allElements.findIndex(e => e.id === b.id);
    return idxA - idxB;
  });

  const baseElement = sortedSelected[0]; // Bottom-most base element
  const topElements = sortedSelected.slice(1); // Upper overlay elements

  // Convert all elements into Polygon rings for geometric clipping
  const allPolygons: Polygon[] = sortedSelected.map(el => elementToPolygon(el));
  const basePolygon: Polygon = allPolygons[0];
  const topPolygons: Polygon[] = allPolygons.slice(1);

  let resultMultiPoly: MultiPolygon = [];
  let fillRule: 'nonzero' | 'evenodd' = 'nonzero';
  let operationName = 'Pathfinder United';
  let isOutlineMode = false;

  try {
    switch (mode) {
      case 'unite':
      case 'merge':
        resultMultiPoly = polygonClipping.union(allPolygons[0], ...allPolygons.slice(1));
        fillRule = 'nonzero';
        operationName = 'Pathfinder Unite';
        break;

      case 'subtract':
        // True Geometric Subtract: Base shape minus all upper overlapping shapes
        resultMultiPoly = polygonClipping.difference(basePolygon, ...topPolygons);
        fillRule = 'evenodd';
        operationName = 'Pathfinder Subtract';
        break;

      case 'minus-back':
        // True Geometric Minus Back: Topmost shape minus all lower shapes
        {
          const topPoly = allPolygons[allPolygons.length - 1];
          const bottomPolies = allPolygons.slice(0, -1);
          resultMultiPoly = polygonClipping.difference(topPoly, ...bottomPolies);
          fillRule = 'evenodd';
          operationName = 'Pathfinder Minus Back';
        }
        break;

      case 'intersect':
      case 'crop':
        // True Geometric Intersection: Only the exact overlapping region
        resultMultiPoly = polygonClipping.intersection(allPolygons[0], ...allPolygons.slice(1));
        fillRule = 'nonzero';
        operationName = mode === 'crop' ? 'Pathfinder Crop' : 'Pathfinder Intersect';
        break;

      case 'exclude':
        // True Geometric XOR: Cuts out intersection, keeps non-overlapping regions
        resultMultiPoly = polygonClipping.xor(allPolygons[0], ...allPolygons.slice(1));
        fillRule = 'evenodd';
        operationName = 'Pathfinder Exclude';
        break;

      case 'divide':
      case 'trim':
        // Divide/Trim
        resultMultiPoly = polygonClipping.union(allPolygons[0], ...allPolygons.slice(1));
        fillRule = 'evenodd';
        operationName = mode === 'trim' ? 'Pathfinder Trim' : 'Pathfinder Divided';
        break;

      case 'outline':
        resultMultiPoly = polygonClipping.union(allPolygons[0], ...allPolygons.slice(1));
        fillRule = 'nonzero';
        operationName = 'Pathfinder Outline';
        isOutlineMode = true;
        break;

      default:
        resultMultiPoly = polygonClipping.union(allPolygons[0], ...allPolygons.slice(1));
        break;
    }
  } catch (err) {
    console.warn('Pathfinder boolean clipping fallback:', err);
    // Safe SVG path fallback if geometric clipping encounters non-manifold geometry
    const baseAbsD = elementToSvgPathD(baseElement);
    const topAbsDs = topElements.map(el => elementToSvgPathD(el));
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    selectedElements.forEach(el => {
      minX = Math.min(minX, el.x || 0);
      minY = Math.min(minY, el.y || 0);
      maxX = Math.max(maxX, (el.x || 0) + (el.width || 40));
      maxY = Math.max(maxY, (el.y || 0) + (el.height || 40));
    });
    const baseRelD = shiftSvgPathCoordinates(baseAbsD, -minX, -minY);
    const topRelDs = topAbsDs.map(d => shiftSvgPathCoordinates(d, -minX, -minY));
    const resultingPathD = [baseRelD, ...topRelDs].join(' ');
    resultMultiPoly = []; // will trigger fallback creation below
  }

  // If geometric operation resulted in valid MultiPolygon, convert to clean SVG path
  let finalPathD = '';
  let finalPoints: Point[] = [];
  let finalX = 0, finalY = 0, finalW = 40, finalH = 40;

  if (resultMultiPoly && resultMultiPoly.length > 0) {
    const res = multiPolygonToPathData(resultMultiPoly);
    if (res.pathD && res.width >= 1 && res.height >= 1) {
      finalPathD = res.pathD;
      finalPoints = res.points;
      finalX = res.minX;
      finalY = res.minY;
      finalW = res.width;
      finalH = res.height;
    }
  }

  // If clipping returned empty (e.g. shapes did not overlap during Intersect):
  if (!finalPathD) {
    // Retain elements rather than destroying geometry
    return allElements;
  }

  // Primary styling from top element
  const primaryEl = sortedSelected[sortedSelected.length - 1];
  const primaryFill = isOutlineMode ? 'none' : (primaryEl.fillColor || primaryEl.backgroundColor || primaryEl.color || baseElement.fillColor || baseElement.backgroundColor || baseElement.color || '#3b82f6');
  const primaryBorder = primaryEl.borderColor || primaryEl.strokeColor || baseElement.borderColor || baseElement.strokeColor || '#1d4ed8';
  const strokeW = primaryEl.borderWidth ?? primaryEl.strokeWidth ?? baseElement.borderWidth ?? baseElement.strokeWidth ?? 2;

  // Create new Pathfinder element positioned EXACTLY at finalX, finalY
  // NOTE: backgroundColor MUST be 'transparent' and isTransparentBg: true so no solid rectangle is drawn!
  const newPathElement: VdpElement = {
    id: `pathfinder-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
    name: operationName,
    type: 'path',
    x: finalX,
    y: finalY,
    width: finalW,
    height: finalH,
    pathData: finalPathD,
    pathD: finalPathD,
    points: finalPoints,
    fillRule,
    fillColor: primaryFill,
    color: primaryFill,
    backgroundColor: 'transparent',
    isTransparentBg: true,
    borderColor: primaryBorder,
    strokeColor: primaryBorder,
    borderWidth: isOutlineMode ? Math.max(2, strokeW) : strokeW,
    strokeWidth: isOutlineMode ? Math.max(2, strokeW) : strokeW,
    opacity: primaryEl.opacity ?? 1,
    rotation: 0,
    zIndex: Math.max(...selectedElements.map(e => e.zIndex || 1))
  };

  // Replace selected elements in template with the new Pathfinder element
  const selectedIds = new Set(selectedElements.map(e => e.id));
  const newElements: VdpElement[] = [];

  let inserted = false;
  allElements.forEach(el => {
    if (selectedIds.has(el.id)) {
      if (!inserted) {
        newElements.push(newPathElement);
        inserted = true;
      }
    } else {
      newElements.push(el);
    }
  });

  return newElements;
}
